import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User, makeAuthenticatedRequest } from '../services/authService';
import { habitService, Habit, HabitCompletion } from '../services/habitService';
import { goalService } from '../services/goalService';
import { Goal } from '../types/Goal';
import { taskService, Task } from '../services/taskService';
import DailyNotesService, { DailyNote } from '../services/dailyNotesService';
import { NotesService, Note } from '../services/notesService';
import { ContentBlock } from '../services/blockService';
import BlockEditor from './notes/BlockEditor';
import { useBlockManager } from '../hooks/useBlockManager';
import { BlockContainer } from '../services/blockManager';
import PageHeader from './common/PageHeader';
import { cashflowService, CashflowEntry } from '../services/cashflowService';
import { getActivityCounts } from '../services/activityService';
import { TimeLogsService } from '../services/timelogsService';
import { stockPortfolioService, StockTransaction } from '../services/stockPortfolioService';
import './Years.css';

interface YearsProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

interface YearlyStats {
  habits: {
    total: number;
    completed: number;
    completionRate: number;
    habits: Habit[];
  };
  goals: {
    total: number;
    active: number;
    completed: number;
    goals: Goal[];
  };
  tasks: {
    total: number;
    completed: number;
    completionRate: number;
    tasks: Task[];
  };
  notes: {
    totalDays: number;
    daysWithNotes: number;
    notes: DailyNote[];
  };
  cashflow: {
    totalSpent: number;
    totalIncome: number;
    netCashflow: number;
    transactions: number;
  };
  blocks: {
    total: number;
  };
  lessons: {
    total: number;
  };
  activities: {
    total: number;
  };
  books: {
    totalPages: number;
    recordCount: number;
  };
  meditation: {
    totalMinutes: number;
    totalHours: number;
    sessionCount: number;
  };
  stocks: {
    buyCount: number;
    sellCount: number;
    totalTransactions: number;
    transactions: StockTransaction[];
  };
}

const Years: React.FC<YearsProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { year } = useParams<{ year?: string }>();
  const [yearlyStats, setYearlyStats] = useState<YearlyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState(0); // 0 = current year, -1 = last year, etc.
  
  // Yearly note state
  const [yearlyNote, setYearlyNote] = useState<Note | null>(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Refs to prevent duplicate loading calls
  const loadingDataRef = useRef<boolean>(false);
  const loadingNoteRef = useRef<boolean>(false);
  
  // Create a ref to hold the current yearly note for the callback
  const yearlyNoteRef = useRef<Note | null>(null);
  
  // Update the ref whenever yearlyNote changes
  useEffect(() => {
    yearlyNoteRef.current = yearlyNote;
  }, [yearlyNote]);

  // Utility functions for year conversion
  const formatYear = (date: Date): string => {
    return date.getFullYear().toString();
  };

  const parseYear = (yearStr: string): number | null => {
    const year = parseInt(yearStr, 10);
    if (isNaN(year) || year < 1900 || year > 2100) return null;
    return year;
  };

  const getCurrentYear = (): string => {
    return formatYear(new Date());
  };

  const getYearOffsetFromYear = (yearStr: string): number => {
    if (yearStr === 'current') return 0;
    
    const parsed = parseYear(yearStr);
    if (!parsed) return 0;
    
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    
    return parsed - currentYear;
  };

  const getYearFromOffset = (offset: number): string => {
    const currentDate = new Date();
    const targetYear = currentDate.getFullYear() + offset;
    return targetYear.toString();
  };

  // Get year start and end dates
  const getYearStartEndDates = (yearOffset: number = 0): { startDate: Date; endDate: Date } => {
    const currentDate = new Date();
    const targetYear = currentDate.getFullYear() + yearOffset;
    
    const startDate = new Date(targetYear, 0, 1); // January 1
    const endDate = new Date(targetYear, 11, 31); // December 31
    
    return { startDate, endDate };
  };

  // Format date for API calls
  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Get user ID
  const getUserId = (): number => {
    return user?.id || 0;
  };

  // Generate year string for note ID
  const getYearId = (yearOffset: number = 0): string => {
    const { startDate } = getYearStartEndDates(yearOffset);
    const year = startDate.getFullYear();
    return `year-${year}`;
  };

  // Generate year display range
  const getYearDisplayRange = (yearOffset: number = 0): string => {
    const { startDate, endDate } = getYearStartEndDates(yearOffset);
    const startDateStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endDateStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startDateStr} - ${endDateStr}`;
  };

  // Block management for yearly note
  const {
    blockManager,
    contentBlocks,
    syncStatus,
    loading: blocksLoading,
    loadBlocks,
    setContainerId
  } = useBlockManager({
    userId: getUserId(),
    containerType: 'note',
    containerId: yearlyNote?.note_id,
    onContainerUpdate: async (container: BlockContainer) => {
      // Use the ref to get the current value, not the captured closure value
      const yearlyNoteValue = yearlyNoteRef.current;
      
      console.log('🔧 Updating yearly note:', container);
      console.log('🔧 Yearly note:', yearlyNoteValue);
      
      if (yearlyNoteValue) {
        const blocks = container.blocks.map((blockId, index) => ({
          block_id: blockId,
          order: index
        }));

        await NotesService.updateNote(yearlyNoteValue.note_id, {
          title: yearlyNoteValue.title,
          blocks,
          properties: yearlyNoteValue.properties
        });
      }
    }
  });

  // Load or create yearly note
  const loadYearlyNote = useCallback(async (yearOffset: number = 0) => {
    if (!user?.id || loadingNoteRef.current) return;

    loadingNoteRef.current = true;
    setNoteLoading(true);
    setError(null);

    try {
      const userId = getUserId();
      const yearId = getYearId(yearOffset);
      const yearDisplayRange = getYearDisplayRange(yearOffset);
      const yearNumber = new Date().getFullYear() + yearOffset;

      try {
        // Try to load existing note
        const existingNote = await NotesService.getNoteById(yearId, userId);
        setYearlyNote(existingNote);
        setContainerId(existingNote.note_id);

        // Load the content blocks
        const blockIds = existingNote.blocks.map(b => b.block_id);
        await loadBlocks(blockIds);

      } catch (error: any) {
        // If note doesn't exist, create it
        if (error.status === 404 || error.message?.includes('not found')) {
          
          const newNote = await NotesService.createNoteWithId(
            userId,
            yearId,
            `Year: ${yearNumber}`,
            []
          );
          
          setYearlyNote(newNote);
          setContainerId(newNote.note_id);
          
        } else {
          throw error;
        }
      }
    } catch (error: any) {
      console.error('Error loading yearly note:', error);
      setError('Failed to load yearly note');
    } finally {
      loadingNoteRef.current = false;
      setNoteLoading(false);
    }
  }, [user?.id, loadBlocks, setContainerId]);

  // Load yearly data
  const loadYearlyData = useCallback(async (yearOffset: number = 0) => {
    if (!user?.id || loadingDataRef.current) return;

    try {
      loadingDataRef.current = true;
      setLoading(true);
      setError(null);

      const userId = getUserId();
      const { startDate, endDate } = getYearStartEndDates(yearOffset);
      const startDateStr = formatDate(startDate);
      const endDateStr = formatDate(endDate);

      // Load all data in parallel
      const [
        habitsResponse,
        completionsResponse,
        goalsResponse,
        tasksResponse,
        notesResponse,
        cashflowResponse,
        blocksResponse,
        lessonsResponse,
        activitiesResponse,
        booksResponse,
        meditationResponse,
        stockTransactionsResponse
      ] = await Promise.all([
        habitService.getUserHabits(userId, { is_active: true }),
        habitService.getCompletionsByDateRange(userId, startDateStr, endDateStr),
        goalService.getUserGoals({ user_id: userId.toString() }),
        taskService.getUserTasks({ user_id: userId.toString() }),
        DailyNotesService.getNotesInRange(userId, startDateStr, endDateStr),
        cashflowService.getCashflowEntries({
          start_date: startDateStr,
          end_date: endDateStr,
          limit: 10000,
          offset: 0
        }),
        makeAuthenticatedRequest(
          `/blocks/user/${userId}/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=year`,
          { method: 'GET' }
        ).catch(() => ({ success: false, data: [] })),
        makeAuthenticatedRequest(
          `/lessons/counts?start_date=${startDateStr}&end_date=${endDateStr}&group_by=year`,
          { method: 'GET' }
        ).catch(() => ({ success: false, counts: [] })),
        getActivityCounts(startDateStr, endDateStr).catch(() => ({ success: false, counts_by_date: {}, total_count: 0 })),
        makeAuthenticatedRequest(
          `/books/user/reading-stats?start_date=${startDateStr}&end_date=${endDateStr}`,
          { method: 'GET' }
        ).catch(() => ({ success: false, total_pages: 0, record_count: 0 })),
        TimeLogsService.getTimeLogsByActivityType(userId, 'meditation', startDateStr, endDateStr).catch(() => []),
        stockPortfolioService.getUserTransactions(userId.toString(), undefined, 10000).catch(() => ({ success: false, transactions: [] }))
      ]);

      // Process habits data
      const habits = habitsResponse.success && habitsResponse.habits ? habitsResponse.habits : [];
      const completionsByDate = completionsResponse.success && completionsResponse.completions_by_date 
        ? completionsResponse.completions_by_date 
        : {};

      // Get all dates in the year
      const yearDates: Date[] = [];
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        yearDates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      let totalHabitCompletions = 0;
      let totalPossibleCompletions = 0;

      habits.forEach(habit => {
        yearDates.forEach(date => {
          const dateStr = formatDate(date);
          const dayCompletions = completionsByDate[dateStr] || [];
          const habitCompletion = dayCompletions.find(c => Number(c.habit_id) === Number(habit.id));
          
          totalPossibleCompletions++;
          if (habitCompletion && habitCompletion.is_completed) {
            totalHabitCompletions++;
          }
        });
      });

      // Process goals data
      const goals = goalsResponse.success && goalsResponse.goals ? goalsResponse.goals : [];
      const activeGoals = goals.filter(goal => goal.status === 'active');
      const completedGoals = goals.filter(goal => goal.status === 'completed');

      // Process tasks data
      // Count tasks created in the year and tasks completed in the year (using timestamps)
      const allTasks = tasksResponse.success && tasksResponse.tasks ? tasksResponse.tasks : [];
      
      // Get date strings for comparison (YYYY-MM-DD format)
      const startDateStrForComparison = formatDate(startDate);
      const endDateStrForComparison = formatDate(endDate);
      
      // Tasks created during the year
      const tasksCreatedInYear = allTasks.filter(task => {
        if (!task.created_at) return false;
        const createdDateStr = formatDate(new Date(task.created_at));
        return createdDateStr >= startDateStrForComparison && createdDateStr <= endDateStrForComparison;
      });
      
      // Tasks completed during the year (using completed_at timestamp)
      const tasksCompletedInYear = allTasks.filter(task => {
        if (!task.completed_at || task.status !== 'completed') return false;
        const completedDateStr = formatDate(new Date(task.completed_at));
        return completedDateStr >= startDateStrForComparison && completedDateStr <= endDateStrForComparison;
      });
      
      const taskCompletionRate = tasksCreatedInYear.length > 0 ? (tasksCompletedInYear.length / tasksCreatedInYear.length) * 100 : 0;

      // Process notes data
      const notes = Array.isArray(notesResponse) ? notesResponse : [];
      const daysWithNotes = notes.length;
      const totalDays = yearDates.length;

      // Process cashflow data
      const cashflowEntries = cashflowResponse.success && cashflowResponse.cashflow ? cashflowResponse.cashflow : [];
      // Calculate spending: exclude income and investment categories, use absolute value
      const totalSpent = cashflowEntries
        .filter(entry => entry.category !== 'income' && entry.category !== 'investment')
        .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);
      // Calculate income: only entries with income category
      const totalIncome = cashflowEntries
        .filter(entry => entry.category === 'income')
        .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);
      const netCashflow = totalIncome - totalSpent;

      // Process blocks data
      const blocksData = blocksResponse.success && blocksResponse.data ? blocksResponse.data : [];
      const totalBlocks = blocksData.reduce((sum: number, item: any) => sum + (parseInt(item.count) || 0), 0);

      // Process lessons data
      const lessonsData = lessonsResponse.success && lessonsResponse.counts ? lessonsResponse.counts : [];
      const totalLessons = lessonsData.reduce((sum: number, item: any) => sum + (parseInt(item.count) || 0), 0);

      // Process activities data
      const activitiesCounts = activitiesResponse.success && activitiesResponse.counts_by_date ? activitiesResponse.counts_by_date : {};
      // Use total_count if available, otherwise sum daily counts
      const totalActivities = activitiesResponse.total_count !== undefined 
        ? activitiesResponse.total_count 
        : Object.values(activitiesCounts).reduce((sum: number, count: any) => sum + (count || 0), 0);

      // Process books data
      const booksData = booksResponse.success && booksResponse.data 
        ? booksResponse.data 
        : { total_pages: 0, record_count: 0 };
      const totalPages = booksData.total_pages || 0;
      const recordCount = booksData.record_count || 0;

      // Process meditation data
      const meditationLogs = Array.isArray(meditationResponse) ? meditationResponse : [];
      const totalMeditationMinutes = meditationLogs.reduce((sum: number, log: any) => sum + (log.duration_minutes || 0), 0);
      const totalMeditationHours = totalMeditationMinutes / 60;
      const meditationSessionCount = meditationLogs.length;

      // Process stock transactions data
      const allStockTransactions = stockTransactionsResponse.success && stockTransactionsResponse.transactions 
        ? stockTransactionsResponse.transactions 
        : [];
      
      // Filter transactions by year date range
      const stockTransactionsInYear = allStockTransactions.filter(transaction => {
        const transactionDateStr = formatDate(new Date(transaction.transaction_date));
        return transactionDateStr >= startDateStrForComparison && transactionDateStr <= endDateStrForComparison;
      });
      
      const buyCount = stockTransactionsInYear.filter(t => t.transaction_type === 'buy').length;
      const sellCount = stockTransactionsInYear.filter(t => t.transaction_type === 'sell').length;
      const totalStockTransactions = stockTransactionsInYear.length;

      const stats: YearlyStats = {
        habits: {
          total: habits.length,
          completed: totalHabitCompletions,
          completionRate: totalPossibleCompletions > 0 ? (totalHabitCompletions / totalPossibleCompletions) * 100 : 0,
          habits: habits
        },
        goals: {
          total: goals.length,
          active: activeGoals.length,
          completed: completedGoals.length,
          goals: goals
        },
        tasks: {
          total: tasksCreatedInYear.length,
          completed: tasksCompletedInYear.length,
          completionRate: taskCompletionRate,
          tasks: allTasks
        },
        notes: {
          totalDays: totalDays,
          daysWithNotes: daysWithNotes,
          notes: notes
        },
        cashflow: {
          totalSpent,
          totalIncome,
          netCashflow,
          transactions: cashflowEntries.length
        },
        blocks: {
          total: totalBlocks
        },
        lessons: {
          total: totalLessons
        },
        activities: {
          total: totalActivities
        },
        books: {
          totalPages: totalPages,
          recordCount: recordCount
        },
        meditation: {
          totalMinutes: totalMeditationMinutes,
          totalHours: totalMeditationHours,
          sessionCount: meditationSessionCount
        },
        stocks: {
          buyCount: buyCount,
          sellCount: sellCount,
          totalTransactions: totalStockTransactions,
          transactions: stockTransactionsInYear
        }
      };

      setYearlyStats(stats);
    } catch (err) {
      console.error('Error loading yearly data:', err);
      setError('Failed to load yearly data');
    } finally {
      loadingDataRef.current = false;
      setLoading(false);
    }
  }, [user?.id]);

  // Sync URL parameter with selectedYear
  useEffect(() => {
    if (year !== undefined) {
      if (year === 'current') {
        // Handle 'current' redirect by navigating to actual current year
        const currentYear = getCurrentYear();
        navigate(`/years/${currentYear}`, { replace: true });
        return;
      }
      
      const yearOffset = getYearOffsetFromYear(year);
      if (yearOffset !== selectedYear) {
        setSelectedYear(yearOffset);
      }
      
      // Validate year format
      if (!parseYear(year)) {
        // Invalid year format, redirect to current year
        const currentYear = getCurrentYear();
        navigate(`/years/${currentYear}`, { replace: true });
      }
    } else {
      // No year parameter, redirect to current year
      const currentYear = getCurrentYear();
      navigate(`/years/${currentYear}`, { replace: true });
    }
  }, [year, selectedYear, navigate]);

  // Load data when component mounts or year changes
  useEffect(() => {
    if (user?.id) {
      Promise.all([
        loadYearlyData(selectedYear),
        loadYearlyNote(selectedYear)
      ]);
    }
  }, [user, selectedYear]);

  // Navigation functions
  const goToPreviousYear = () => {
    const newYearOffset = selectedYear - 1;
    const yearStr = getYearFromOffset(newYearOffset);
    navigate(`/years/${yearStr}`);
  };

  const goToNextYear = () => {
    const newYearOffset = selectedYear + 1;
    const yearStr = getYearFromOffset(newYearOffset);
    navigate(`/years/${yearStr}`);
  };

  const goToCurrentYear = () => {
    const currentYear = getCurrentYear();
    navigate(`/years/${currentYear}`);
  };

  // Get year display info
  const getYearDisplayInfo = () => {
    const { startDate, endDate } = getYearStartEndDates(selectedYear);
    
    const isCurrentYear = selectedYear === 0;
    const yearLabel = isCurrentYear ? 'This Year' : 
                     selectedYear === -1 ? 'Last Year' :
                     selectedYear === 1 ? 'Next Year' :
                     `${Math.abs(selectedYear)} Year${Math.abs(selectedYear) > 1 ? 's' : ''} ${selectedYear < 0 ? 'Ago' : 'Ahead'}`;

    return {
      yearLabel,
      startDate,
      endDate,
      isCurrentYear
    };
  };

  const yearInfo = getYearDisplayInfo();

  // Formatting helper functions
  const formatMeditationHours = (hours: number): string => {
    if (hours === 0) return '0 hours';
    // Round to 1 decimal place
    const roundedHours = Math.round(hours * 10) / 10;
    return `${roundedHours} hour${roundedHours !== 1 ? 's' : ''}`;
  };

  const formatBookPages = (pages: number, sessions: number): string => {
    if (pages === 0) return '';
    if (sessions > 1) {
      return `read ${pages} pages across ${sessions} reading sessions`;
    }
    return `read ${pages} page${pages !== 1 ? 's' : ''}`;
  };

  // Generate year description paragraph
  const generateYearDescription = (stats: YearlyStats | null, yearOffset: number): string => {
    if (!stats) {
      return '';
    }

    const { startDate, endDate } = getYearStartEndDates(yearOffset);
    const startDateStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endDateStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    
    const yearIdentifier = year && year !== 'current' ? year : getCurrentYear();
    const yearNumber = parseInt(yearIdentifier);
    const totalPossibleHabitCompletions = stats.habits.total * stats.notes.totalDays;
    
    const parts: string[] = [];
    
    // Year identifier and date range
    parts.push(`This is ${yearNumber} (${yearIdentifier}), starting from ${startDateStr} to ${endDateStr}.`);
    
    // Habits
    if (totalPossibleHabitCompletions > 0) {
      parts.push(`In this year, you completed ${stats.habits.completed} out of ${totalPossibleHabitCompletions} possible habit completions (${stats.habits.completionRate.toFixed(1)}%)`);
    } else {
      parts.push(`In this year, you have ${stats.habits.total} active habit${stats.habits.total !== 1 ? 's' : ''}`);
    }
    
    // Tasks
    if (stats.tasks.total > 0) {
      parts.push(`completed ${stats.tasks.completed} out of ${stats.tasks.total} task${stats.tasks.total !== 1 ? 's' : ''} (${stats.tasks.completionRate.toFixed(1)}%)`);
    } else {
      parts.push(`completed 0 tasks`);
    }
    
    // Goals - only mention in_progress goals
    if (stats.goals.active > 0) {
      parts.push(`maintained ${stats.goals.active} active goal${stats.goals.active !== 1 ? 's' : ''}`);
    }
    
    // Notes
    parts.push(`wrote notes on ${stats.notes.daysWithNotes} out of ${stats.notes.totalDays} day${stats.notes.totalDays !== 1 ? 's' : ''}`);
    
    // Cashflow
    if (stats.cashflow.transactions > 0) {
      const cashflowParts: string[] = [];
      if (stats.cashflow.totalIncome > 0) {
        cashflowParts.push(`earned ${stats.cashflow.totalIncome.toLocaleString()}`);
      }
      // Always mention spending if there are transactions
      if (stats.cashflow.totalSpent > 0) {
        cashflowParts.push(`spent ${stats.cashflow.totalSpent.toLocaleString()}`);
      }
      if (cashflowParts.length > 0) {
        if (cashflowParts.length > 1) {
          parts.push(`${cashflowParts.join(' and ')}, with a net cashflow of ${stats.cashflow.netCashflow >= 0 ? '+' : ''}${stats.cashflow.netCashflow.toLocaleString()}`);
        } else {
          parts.push(cashflowParts[0]);
        }
      }
    }
    
    // Blocks
    if (stats.blocks.total > 0) {
      parts.push(`created ${stats.blocks.total} block${stats.blocks.total !== 1 ? 's' : ''}`);
    }
    
    // Lessons
    if (stats.lessons.total > 0) {
      parts.push(`generated ${stats.lessons.total} lesson${stats.lessons.total !== 1 ? 's' : ''}`);
    }
    
    // Activities
    if (stats.activities.total > 0) {
      parts.push(`performed ${stats.activities.total} activit${stats.activities.total !== 1 ? 'ies' : 'y'}`);
    }
    
    // Books
    if (stats.books.totalPages > 0) {
      const bookPagesText = formatBookPages(stats.books.totalPages, stats.books.recordCount);
      if (bookPagesText) {
        parts.push(bookPagesText);
      }
    }
    
    // Meditation
    if (stats.meditation.totalHours > 0) {
      parts.push(`meditated for ${formatMeditationHours(stats.meditation.totalHours)}`);
    }
    
    // Stock transactions
    if (stats.stocks.totalTransactions > 0) {
      const stockParts: string[] = [];
      if (stats.stocks.buyCount > 0) {
        stockParts.push(`bought ${stats.stocks.buyCount} stock${stats.stocks.buyCount !== 1 ? 's' : ''}`);
      }
      if (stats.stocks.sellCount > 0) {
        stockParts.push(`sold ${stats.stocks.sellCount} stock${stats.stocks.sellCount !== 1 ? 's' : ''}`);
      }
      if (stockParts.length > 0) {
        if (stockParts.length > 1) {
          parts.push(stockParts.join(' and '));
        } else {
          parts.push(stockParts[0]);
        }
      }
    }
    
    return parts.join(', ') + '.';
  };

  if (loading) {
    return (
      <div className="notes-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<span>Yearly Progress</span>}
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading yearly data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notes-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<span>Yearly Progress</span>}
        />
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="retry-btn" onClick={() => loadYearlyData(selectedYear)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="notes-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <div className="year-navigation">
            <button className="nav-btn" onClick={goToPreviousYear}>←</button>
            <div className="year-info">
              {year && year !== 'current' && (
                <p className="year-label">{year}</p>
              )}
            </div>
            <button className="nav-btn" onClick={goToNextYear}>→</button>
            {!yearInfo.isCurrentYear && (
              <button className="current-year-btn" onClick={goToCurrentYear}>
                Current Year
              </button>
            )}
          </div>
        }
        rightActions={
          saving ? (
            <span className="years-saving-indicator">
              <div className="saving-spinner"></div>
              Saving...
            </span>
          ) : undefined
        }
      />

      <div className="years-content">
        {/* Year Description */}
        {yearlyStats && (
          <div className="year-description">
            {generateYearDescription(yearlyStats, selectedYear)}
          </div>
        )}

        {/* Yearly Note Editor */}
        <div className="yearly-note-section">
          <div className="yearly-note-editor">
            {noteLoading ? (
              <div className="note-loading">
                <div className="loading-spinner"></div>
                <p>Loading yearly note...</p>
              </div>
            ) : (
              <BlockEditor
                blockManager={blockManager}
                blocks={contentBlocks}
                placeholder={`Write your notes for ${getYearDisplayRange(selectedYear)}...`}
                onSyncStatusChange={() => {}} // Sync status is handled by hook
              />
            )}
          </div>
        </div>
      </div>

      {/* Sync Status Icon */}
      <div className={`years-sync-status-icon ${syncStatus}`}>
        {syncStatus === 'syncing' && <div className="years-sync-spinner">⟳</div>}
        {syncStatus === 'synced' && <div className="years-sync-check">✓</div>}
        {syncStatus === 'error' && <div className="years-sync-error">⚠</div>}
      </div>

    </div>
  );
};

export default Years;

